import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sentry } from "@/lib/sentry";
import { useLanguage } from "@/i18n/LanguageProvider";

// Route-level error boundary. Wraps the routed page content (inside the
// providers + BrowserRouter, below LanguageProvider) so a render crash in ONE
// screen shows a recoverable, premium fallback instead of white-screening the
// whole app and dropping the tester's session. The app-level Sentry boundary in
// main.tsx stays as a last-resort catch-all for crashes ABOVE the router.
//
// Reporting: Sentry.ErrorBoundary auto-captures the error WITH the React
// component stack (so we see which component/line broke, not a cryptic bundle
// frame). No manual capture needed; no new dependency (@sentry/react already in).
//
// Scope: render errors only — async/handler errors are not caught by React
// boundaries (by design; this targets white-screens).

// Premium VYV-dark fallback. Rendered under LanguageProvider, so it can i18n.
function RouteErrorFallback({ resetError }: { resetError: () => void }) {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleReset = () => {
    // Clear the boundary's error state, then route home. The key={pathname}
    // on the boundary also remounts it on any navigation, so moving to another
    // module always clears a stuck screen.
    resetError();
    navigate("/home", { replace: true });
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#06111C",
        color: "#e5e7eb",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        textAlign: "center",
        gap: "0.75rem",
      }}
    >
      <h1
        style={{
          fontSize: "1.05rem",
          fontWeight: 600,
          color: "hsl(187 72% 60%)",
          margin: 0,
        }}
      >
        {t("errorBoundary.title")}
      </h1>
      <p
        style={{
          fontSize: "0.85rem",
          lineHeight: 1.5,
          opacity: 0.65,
          maxWidth: "320px",
          margin: 0,
        }}
      >
        {t("errorBoundary.body")}
      </p>
      <button
        type="button"
        onClick={handleReset}
        style={{
          marginTop: "1.25rem",
          background: "hsl(187 72% 50% / 0.16)",
          border: "1px solid hsl(187 72% 50% / 0.35)",
          color: "hsl(187 72% 65%)",
          padding: "0.7rem 1.4rem",
          borderRadius: "14px",
          fontSize: "0.85rem",
          fontWeight: 600,
          cursor: "pointer",
          boxShadow: "0 0 20px hsl(187 72% 50% / 0.12)",
        }}
      >
        {t("errorBoundary.action")}
      </button>
    </div>
  );
}

export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  // Remount (and thus reset) the boundary whenever the route changes, so
  // navigating away from a broken screen clears the error automatically.
  const { pathname } = useLocation();
  return (
    <Sentry.ErrorBoundary
      key={pathname}
      showDialog={false}
      fallback={({ resetError }) => <RouteErrorFallback resetError={resetError} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
